import { useState } from 'react';
import { Crosshair, Clock, Network, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { useCanaryHits, useCanaryHitDetail } from '../lib/hooks';

function bandClass(band: string): string {
  if (band === 'sub_2s') return 'bg-violet-500/20 text-violet-300 border-violet-500/40';
  if (band === '2s_to_30s') return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
  return 'bg-sky-500/20 text-sky-300 border-sky-500/40';
}

export function Canaries() {
  const { isConnected } = useAuth();
  const { data, isLoading, refetch } = useCanaryHits();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: detail } = useCanaryHitDetail(selectedId);

  const hits = data?.hits ?? [];

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-4">Prompt-Injection Canaries</h2>
        <p className="text-gray-500">Connect to the control plane in Settings</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crosshair className="w-6 h-6 text-violet-400" />
            Prompt-Injection Canaries
          </h2>
          <p className="text-gray-400 mt-1 text-sm">
            Unlinked per-session paths embedded as Important Message config-migration cues.
            A hit means content was parsed and the instruction was acted on — not a crawl.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-400 hover:text-white"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mb-4 text-xs text-gray-500 border border-white/5 rounded-lg px-3 py-2 bg-black/20">
        Timing labels describe observed latency only — Sub-2s / mid-band / 30s+ — not conclusions
        about actor type.
      </div>

      {hits.length === 0 ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Crosshair className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">
            {isLoading ? 'Loading…' : 'No canary-path hits yet (synthetic excluded)'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {hits.map((h) => {
            const open = selectedId === h.id;
            return (
              <div
                key={h.id}
                className="bg-sg-card border border-sg-border rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(open ? null : h.id)}
                  className="w-full text-left px-4 py-3 flex flex-wrap items-center justify-between gap-2 hover:bg-white/[0.02]"
                >
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${bandClass(h.timingBand)}`}
                    >
                      {h.timingLabel}
                    </span>
                    <span className="text-sm text-white font-mono truncate max-w-md">
                      {h.canaryPath}
                    </span>
                    <span className="text-xs text-gray-500">
                      from decoy {h.decoyPath || h.deceptionId.slice(0, 8)}
                      {h.decoyVariant ? ` (${h.decoyVariant})` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {h.deltaMs}ms
                    </span>
                    <span className="flex items-center gap-1">
                      <Network className="w-3 h-3" />
                      {h.sourceIp || '—'}
                      {h.sourceAsn != null ? ` AS${h.sourceAsn}` : ''}
                    </span>
                    {open ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/5 px-4 py-4 space-y-4 bg-black/20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <Fact label="Delta" value={`${h.deltaMs} ms — ${h.timingLabel}`} />
                      <Fact label="Timing band" value={h.timingBand} />
                      <Fact label="Originating decoy path" value={h.decoyPath || '—'} mono />
                      <Fact label="Decoy variant" value={h.decoyVariant || '—'} />
                      <Fact label="Decoy trackingId" value={h.deceptionId} mono />
                      <Fact label="Canary path" value={h.canaryPath} mono />
                      <Fact label="Source IP" value={h.sourceIp || '—'} mono />
                      <Fact
                        label="ASN"
                        value={
                          h.sourceAsn != null
                            ? `${h.sourceAsn}${h.sourceAsOrg ? ` (${h.sourceAsOrg})` : ''}`
                            : '—'
                        }
                      />
                      <Fact label="User-Agent" value={h.userAgent || '—'} />
                      <Fact
                        label="Breadcrumbs before / after"
                        value={`${h.breadcrumbsBefore} / ${h.breadcrumbsAfter}`}
                      />
                      <Fact label="Signal type" value={h.signalType} mono />
                      <Fact label="Confidence" value={String(h.confidence)} />
                      <Fact
                        label="Header order"
                        value={
                          h.headerOrder?.length
                            ? h.headerOrder.join(' → ')
                            : '—'
                        }
                        mono
                      />
                      <Fact
                        label="Hit at"
                        value={h.createdAt ? new Date(h.createdAt).toLocaleString() : '—'}
                      />
                    </div>

                    <div>
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                        Session timeline
                      </div>
                      {!detail ? (
                        <p className="text-xs text-gray-600">Loading timeline…</p>
                      ) : (
                        <ul className="space-y-1 max-h-64 overflow-y-auto font-mono text-xs">
                          {(detail.timeline || []).map((t: any) => {
                            const isOrigin = t.id === h.deceptionId;
                            return (
                              <li
                                key={t.id}
                                className={`px-2 py-1 rounded ${
                                  isOrigin
                                    ? 'bg-violet-500/15 text-violet-200 border border-violet-500/30'
                                    : 'text-gray-400'
                                }`}
                              >
                                <span className="text-gray-600 mr-2">{t.created_at}</span>
                                d={t.depth} {t.deception_type}/{t.variant || '—'} {t.path}
                                {isOrigin ? ' ← originating decoy' : ''}
                              </li>
                            );
                          })}
                          <li className="px-2 py-1 rounded bg-violet-500/25 text-violet-100 border border-violet-400/40">
                            canary fetch {h.canaryPath} (+{h.deltaMs}ms) — {h.timingLabel}
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">{label}</div>
      <div className={`text-gray-200 break-all ${mono ? 'font-mono text-xs' : 'text-sm'}`}>
        {value}
      </div>
    </div>
  );
}
